'use client';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Avatar, Box, FormControlLabel, IconButton, Switch, TextField, Typography } from '@mui/material';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  type MRT_SortingState,
  type MRT_VisibilityState,
} from 'material-react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  queryInventoryAuditorGlobalCatalog,
  type InventoryAuditorItemRow,
} from '@/hooks/useInventoryAuditorInventory';
import { useNotification } from '@/contexts/NotificationContext';
import { useAuthData } from '@/hooks/getAuthData';
import { isElevatedRole } from '@/utils/roles';
import { inventoryAuditorCatalogOriginLabel } from '@/stack-shared/constants/inventory-auditor-catalog.constants';
import InventoryAuditorCatalogItemEditDialog from '../inventory-auditor/InventoryAuditorCatalogItemEditDialog';
import TitleBar from '../layout/TitleBar';

/** Prefer UPC-A (12) when stored as zero-padded EAN-13. */
function displayUpc(raw: string | null | undefined): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '—';
  if (digits.length === 13 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

const SORTABLE = new Set(['upc', 'name', 'id', 'createdAt', 'updatedAt', 'deleted']);

const VISIBILITY_STORAGE_KEY = 'inventoryAuditorItemCatalogPage_columnVisibility';
const SORTING_STORAGE_KEY = 'inventoryAuditorItemCatalogPage_sorting';
const SHOW_DELETED_STORAGE_KEY = 'inventoryAuditorItemCatalogPage_showDeleted';

const DEFAULT_COLUMN_VISIBILITY: MRT_VisibilityState = {
  description: false,
  scanContext: false,
  createdAt: false,
  updatedAt: false,
  deleted: false,
};

const DEFAULT_SORTING: MRT_SortingState = [{ id: 'upc', desc: false }];

function readStoredJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function loadColumnVisibility(): MRT_VisibilityState {
  const stored = readStoredJson<MRT_VisibilityState>(VISIBILITY_STORAGE_KEY);
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return { ...DEFAULT_COLUMN_VISIBILITY };
  }
  return { ...DEFAULT_COLUMN_VISIBILITY, ...stored };
}

function loadSorting(): MRT_SortingState {
  const stored = readStoredJson<MRT_SortingState>(SORTING_STORAGE_KEY);
  if (!Array.isArray(stored) || stored.length === 0) return DEFAULT_SORTING;
  const first = stored[0];
  if (!first || typeof first !== 'object' || typeof first.id !== 'string' || !SORTABLE.has(first.id)) {
    return DEFAULT_SORTING;
  }
  return [{ id: first.id, desc: Boolean(first.desc) }];
}

function loadShowDeleted(): boolean {
  const stored = readStoredJson<unknown>(SHOW_DELETED_STORAGE_KEY);
  return stored === true;
}

function formatCatalogDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

const InventoryAuditorItemCatalogPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState<MRT_SortingState>(loadSorting);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(loadColumnVisibility);
  const [showDeleted, setShowDeleted] = useState(loadShowDeleted);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<InventoryAuditorItemRow[]>([]);
  const [total, setTotal] = useState(0);
  const { showError, showSuccess } = useNotification();
  const { roles } = useAuthData();
  const isAdmin = roles.some((r) => isElevatedRole(r));
  const [editing, setEditing] = useState<InventoryAuditorItemRow | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [debounced, showDeleted]);

  useEffect(() => {
    localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    localStorage.setItem(SORTING_STORAGE_KEY, JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    localStorage.setItem(SHOW_DELETED_STORAGE_KEY, JSON.stringify(showDeleted));
  }, [showDeleted]);

  const orderBy = sorting[0]?.id && SORTABLE.has(sorting[0].id) ? sorting[0].id : 'upc';
  const orderDirection = sorting[0]?.desc ? 'DESC' : 'ASC';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await queryInventoryAuditorGlobalCatalog({
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search: debounced,
        orderBy,
        orderDirection,
        showDeleted,
      });
      setRows(r.data);
      setTotal(r.total);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load catalog');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, debounced, orderBy, orderDirection, showDeleted, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<MRT_ColumnDef<InventoryAuditorItemRow>[]>(
    () => [
      ...(isAdmin
        ? [
            {
              id: 'actions',
              header: '',
              size: 48,
              enableSorting: false,
              enableColumnFilter: false,
              enableHiding: false,
              Cell: ({ row }) => (
                <IconButton
                  size="small"
                  aria-label="Edit catalog item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(row.original);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ),
            } satisfies MRT_ColumnDef<InventoryAuditorItemRow>,
          ]
        : []),
      {
        id: 'image',
        header: '',
        size: 56,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Avatar variant="rounded" src={row.original.imageUrl || undefined} sx={{ width: 40, height: 40 }}>
            <Inventory2Icon fontSize="small" />
          </Avatar>
        ),
      },
      {
        accessorKey: 'upc',
        header: 'UPC',
        size: 168,
        Cell: ({ cell }) => {
          const upc = displayUpc(cell.getValue<string>());
          return (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="body2" component="span">
                {upc}
              </Typography>
              {upc !== '—' ? (
                <IconButton
                  size="small"
                  aria-label="Copy UPC"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await navigator.clipboard.writeText(upc);
                      showSuccess('UPC copied');
                    } catch {
                      showError('Could not copy UPC');
                    }
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              ) : null}
            </Box>
          );
        },
      },
      {
        accessorKey: 'pdiNumber',
        header: 'PDI #',
        size: 96,
        Cell: ({ cell }) => cell.getValue<string | null>()?.trim() || '—',
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 280,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2">{row.original.name?.trim() || '—'}</Typography>
            {row.original.catalogOrigin === 'scanned' && row.original.scanContext?.trim() ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {row.original.scanContext.trim()}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        id: 'catalogOrigin',
        accessorKey: 'catalogOrigin',
        header: 'Source',
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => inventoryAuditorCatalogOriginLabel(row.original.catalogOrigin),
      },
      {
        id: 'scanContext',
        accessorKey: 'scanContext',
        header: 'Context',
        size: 200,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>()?.trim() || '—',
      },
      {
        accessorKey: 'uomPackType',
        header: 'UOM',
        size: 100,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      },
      {
        accessorKey: 'size',
        header: 'Size',
        size: 100,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 220,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>()?.trim() || '—',
      },
      {
        accessorKey: 'createdAt',
        header: 'Date created',
        size: 160,
        enableHiding: true,
        Cell: ({ cell }) => formatCatalogDate(cell.getValue<string | undefined>()),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Date updated',
        size: 160,
        enableHiding: true,
        Cell: ({ cell }) => formatCatalogDate(cell.getValue<string | undefined>()),
      },
      {
        accessorKey: 'deleted',
        header: 'Deleted',
        size: 92,
        enableHiding: true,
        Cell: ({ cell }) => (cell.getValue<boolean>() ? 'Yes' : '—'),
      },
    ],
    [isAdmin, showError, showSuccess],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Item catalog' },
  ];

  return (
    <>
      <TitleBar breadcrumbs={breadcrumbs} title="Item catalog" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every SKU ever added to Inventory Auditor (from scans or admin). Location on-hand quantities are not shown
          here; use Store count or a location&apos;s inventory tab for counts.
        </Typography>
        <MaterialReactTable
          columns={columns}
          data={rows}
          rowCount={total}
          manualPagination
          manualSorting
          enableGlobalFilter={false}
          enableColumnFilters={false}
          enableDensityToggle
          enableFullScreenToggle
          enableHiding
          enableColumnResizing
          enableStickyHeader
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          onColumnVisibilityChange={setColumnVisibility}
          initialState={{
            density: 'compact',
            columnVisibility,
          }}
          state={{
            isLoading: loading,
            pagination,
            sorting,
            columnVisibility,
          }}
          muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 260px)' } }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => navigate(`/app/inventory-auditor/catalog/${row.original.id}`),
            sx: { cursor: 'pointer', opacity: row.original.deleted ? 0.55 : 1 },
          })}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search UPC / name / description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 320 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showDeleted}
                    onChange={(_, v) => setShowDeleted(v)}
                  />
                }
                label="Show deleted"
              />
            </Box>
          )}
        />
        <InventoryAuditorCatalogItemEditDialog
          open={editing != null}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            if (updated.deleted && !showDeleted) {
              setRows((prev) => prev.filter((r) => r.id !== updated.id));
              setTotal((t) => Math.max(0, t - 1));
            } else {
              setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
            }
          }}
        />
      </Box>
    </>
  );
};

export default InventoryAuditorItemCatalogPage;

